
import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase, type Speaker } from '@/integrations/supabase/client';
import { 
  Card, 
  CardContent,
  CardFooter,
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Pencil, Trash2, Plus, X, Check, User, Briefcase } from "lucide-react";

const initialSpeakerState: Omit<Speaker, 'id'> = {
  name: '',
  slug: '',
  title: '',
  image_url: '',
  bio: '',
  topic: null,
  company_name: null,
  company_logo: null,
  twitter_url: null,
  linkedin_url: null,
  github_url: null,
  conference_id: null,
  created_at: undefined,
  updated_at: undefined,
};

export interface SpeakersAdminProps {
  conferenceId: string | undefined;
}

const SpeakersAdmin: React.FC<SpeakersAdminProps> = ({ conferenceId }) => {
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Omit<Speaker, 'id'> & { id?: string }>(initialSpeakerState);
  const [isEditing, setIsEditing] = useState(false);
  const { toast } = useToast();

  const fetchSpeakers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('speakers')
        .select('*')
        .order('name');

      if (error) throw error;
      
      if (data && data.length > 0) {
        // Map the data to ensure all required fields are present
        const mappedSpeakers: Speaker[] = data.map(speaker => ({
          id: speaker.id,
          name: speaker.name,
          slug: speaker.name.toLowerCase().replace(/\s+/g, '-'), // Generate slug
          title: speaker.title || '',
          image_url: speaker.image_url || null,
          bio: '', // Add empty bio
          topic: speaker.topic || null,
          company_name: speaker.company_name || null,
          company_logo: speaker.company_logo || null,
          twitter_url: speaker.twitter_url || null,
          linkedin_url: speaker.linkedin_url || null,
          github_url: speaker.github_url || null,
          conference_id: speaker.conference_id,
          created_at: speaker.created_at,
          updated_at: speaker.updated_at
        }));
        setSpeakers(mappedSpeakers);
      } else {
        setSpeakers([]);
      }
    } catch (error) {
      console.error('Error fetching speakers:', error);
      toast({
        title: "Error",
        description: "Failed to load speakers data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpeakers();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData(initialSpeakerState);
    setIsEditing(false);
    setShowForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Generate slug from name if not provided
      const slug = formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-');
      
      if (isEditing && formData.id) {
        const { error } = await supabase
          .from('speakers')
          .update({
            name: formData.name,
            title: formData.title,
            image_url: formData.image_url || null,
            topic: formData.topic || null,
            company_name: formData.company_name || null,
            company_logo: formData.company_logo || null,
            twitter_url: formData.twitter_url || null,
            linkedin_url: formData.linkedin_url || null,
            github_url: formData.github_url || null
            // Note: slug and bio are not in the database table, so we don't include them in the update
          })
          .eq('id', formData.id);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Speaker updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('speakers')
          .insert([{
            name: formData.name,
            title: formData.title,
            image_url: formData.image_url || null,
            topic: formData.topic || null,
            company_name: formData.company_name || null,
            company_logo: formData.company_logo || null,
            twitter_url: formData.twitter_url || null,
            linkedin_url: formData.linkedin_url || null,
            github_url: formData.github_url || null
            // Note: slug and bio are not in the database table, so we don't include them in the insert
          }]);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Speaker added successfully",
        });
      }
      
      resetForm();
      fetchSpeakers();
    } catch (error) {
      console.error('Error saving speaker:', error);
      toast({
        title: "Error",
        description: "Failed to save speaker data",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (speaker: Speaker) => {
    setFormData(speaker);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this speaker?')) return;
    
    try {
      const { error } = await supabase
        .from('speakers')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Speaker deleted successfully",
      });
      fetchSpeakers();
    } catch (error) {
      console.error('Error deleting speaker:', error);
      toast({
        title: "Error",
        description: "Failed to delete speaker",
        variant: "destructive",
      });
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Manage Speakers</h2>
        {!showForm && (
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="mr-2 h-4 w-4" /> Add Speaker
          </Button>
        )}
      </div>

      {showForm ? (
        <Card className="mb-8 bg-white/5 border-purple-500/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white">
              {isEditing ? 'Edit Speaker' : 'Add New Speaker'}
            </CardTitle>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium text-white">
                    Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Full name"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="title" className="text-sm font-medium text-white">
                    Title/Position *
                  </label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    placeholder="Job title or position"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="image_url" className="text-sm font-medium text-white">
                    Profile Image URL
                  </label>
                  <Input
                    id="image_url"
                    name="image_url"
                    value={formData.image_url || ''}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="topic" className="text-sm font-medium text-white">
                    Speaking Topic
                  </label>
                  <Input
                    id="topic"
                    name="topic"
                    value={formData.topic || ''}
                    onChange={handleInputChange}
                    placeholder="Topic of presentation"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="company_name" className="text-sm font-medium text-white">
                    Company Name
                  </label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name || ''}
                    onChange={handleInputChange}
                    placeholder="Company or organization"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="company_logo" className="text-sm font-medium text-white">
                    Company Logo
                  </label>
                  <Input
                    id="company_logo"
                    name="company_logo"
                    value={formData.company_logo || ''}
                    onChange={handleInputChange}
                    placeholder="Logo text or URL"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="twitter_url" className="text-sm font-medium text-white">
                    Twitter URL
                  </label>
                  <Input
                    id="twitter_url"
                    name="twitter_url"
                    value={formData.twitter_url || ''}
                    onChange={handleInputChange}
                    placeholder="https://twitter.com/username"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="linkedin_url" className="text-sm font-medium text-white">
                    LinkedIn URL
                  </label>
                  <Input
                    id="linkedin_url"
                    name="linkedin_url"
                    value={formData.linkedin_url || ''}
                    onChange={handleInputChange}
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="github_url" className="text-sm font-medium text-white">
                    GitHub URL
                  </label>
                  <Input
                    id="github_url"
                    name="github_url"
                    value={formData.github_url || ''}
                    onChange={handleInputChange}
                    placeholder="https://github.com/username"
                  />
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button 
                type="button"
                variant="outline" 
                onClick={resetForm}
              >
                <X className="mr-2 h-4 w-4" /> Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Check className="mr-2 h-4 w-4" /> {isEditing ? 'Update' : 'Save'}
              </Button>
            </CardFooter>
          </form>
        </Card>
      ) : null}

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500 mb-4"></div>
          <p className="text-white">Loading speakers...</p>
        </div>
      ) : speakers.length > 0 ? (
        <Card className="bg-white/5 border-purple-500/20 backdrop-blur-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-white/5 border-b border-white/10">
                  <TableHead className="text-white">Name</TableHead>
                  <TableHead className="text-white">Title</TableHead>
                  <TableHead className="text-white">Company</TableHead>
                  <TableHead className="text-white">Topic</TableHead>
                  <TableHead className="text-white w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {speakers.map((speaker) => (
                  <TableRow key={speaker.id} className="hover:bg-white/5 border-b border-white/10">
                    <TableCell className="font-medium text-white flex items-center gap-2">
                      {speaker.image_url ? (
                        <img 
                          src={speaker.image_url} 
                          alt={speaker.name} 
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <User className="h-6 w-6 text-gray-400" />
                      )}
                      {speaker.name}
                    </TableCell>
                    <TableCell className="text-gray-300">{speaker.title}</TableCell>
                    <TableCell className="text-gray-300 flex items-center gap-2">
                      {speaker.company_logo ? (
                        <div className="h-6 w-6 bg-purple-800 flex items-center justify-center rounded text-xs text-white font-medium">
                          {speaker.company_logo}
                        </div>
                      ) : speaker.company_name ? (
                        <Briefcase className="h-4 w-4 text-gray-400" />
                      ) : null}
                      {speaker.company_name}
                    </TableCell>
                    <TableCell className="text-gray-300">{speaker.topic}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleEdit(speaker)}
                          className="text-white hover:text-purple-400 hover:bg-white/10"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(speaker.id)}
                          className="text-white hover:text-red-400 hover:bg-white/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <div className="text-center py-12 bg-white/5 rounded-lg border border-purple-500/20">
          <User className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No Speakers Found</h3>
          <p className="text-gray-400 mb-6">Get started by adding your first speaker</p>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="mr-2 h-4 w-4" /> Add Speaker
          </Button>
        </div>
      )}
    </div>
  );
};

export default SpeakersAdmin;
